import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import api from '../../config/api';
import { brandService } from '../../services/brandService';

const GoogleAdsSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  
  // Multi-brand support
  const [brands, setBrands] = useState([]);
  const [accountBrandMapping, setAccountBrandMapping] = useState({});

  // Parse URL parameters
  const urlParams = new URLSearchParams(location.search);
  const tempId = urlParams.get('temp_id');
  const brandId = urlParams.get('brand_id');

  useEffect(() => {
    if (!tempId) {
      setError('Missing temporary ID. Please restart the OAuth process.');
      setLoading(false);
      return;
    }

    fetchAccounts();
    fetchBrands();
  }, [tempId]);

  const fetchBrands = async () => {
    try {
      const brandsData = await brandService.getBrands();
      setBrands(brandsData || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/api/connectors/google/ads/accounts', {
        params: { temp_id: tempId }
      });

      if (response.data.success) {
        setAccounts(response.data.accounts);
      } else {
        setError(response.data.message || 'Failed to fetch accounts');
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError(err.response?.data?.message || 'Failed to fetch Google Ads accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelection = (customerId, checked) => {
    if (checked) {
      const account = accounts.find(a => a.customerId === customerId);
      setSelectedAccounts([...selectedAccounts, account]);
      // For already-connected accounts, default to their existing brand (re-auth flow).
      // Otherwise use the brand from the OAuth URL params.
      const defaultBrand = account?.alreadyConnected && account?.connectedBrandId
        ? String(account.connectedBrandId)
        : brandId;
      setAccountBrandMapping(prev => ({
        ...prev,
        [customerId]: defaultBrand
      }));
    } else {
      setSelectedAccounts(selectedAccounts.filter(a => a.customerId !== customerId));
      // Remove brand mapping
      setAccountBrandMapping(prev => {
        const newMapping = { ...prev };
        delete newMapping[customerId];
        return newMapping;
      });
    }
  };

  const handleBrandChange = (customerId, newBrandId) => {
    setAccountBrandMapping(prev => ({
      ...prev,
      [customerId]: newBrandId
    }));
  };

  const handleCreateConnectors = async () => {
    if (selectedAccounts.length === 0) {
      setError('Please select at least one account to connect');
      return;
    }

    // Validate brands
    const accountsWithoutBrands = selectedAccounts.filter(account => 
      !accountBrandMapping[account.customerId]
    );
    if (accountsWithoutBrands.length > 0) {
      setError(`Please select a brand for all selected accounts`);
      return;
    }

    // Guard against a missing/blank Google Ads account id — creating a connector with a
    // blank customer_id produces a zombie that bypasses duplicate detection. The backend
    // also rejects this; this gives immediate feedback before the POST.
    const accountsWithoutId = selectedAccounts.filter(account =>
      !account.customerId || String(account.customerId).trim() === ''
    );
    if (accountsWithoutId.length > 0) {
      setError('One or more selected accounts have no valid Google Ads account id. Please reload and try again.');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const createdConnectors = [];

      for (const account of selectedAccounts) {
        const selectedBrandId = accountBrandMapping[account.customerId] || brandId;
        const response = await api.post('/api/connectors/google/ads/create', {
          temp_id: tempId,
          brand_id: parseInt(selectedBrandId),
          customer_id: account.customerId,
          account_name: account.descriptiveName,
          currency_code: account.currencyCode
        });

        if (response.data.success) {
          createdConnectors.push(response.data.connector);
        } else {
          throw new Error(`Failed to create connector for ${account.descriptiveName}: ${response.data.message}`);
        }
      }

      // Get unique brand names
      const uniqueBrands = [...new Set(selectedAccounts.map(account => 
        getBrandName(accountBrandMapping[account.customerId] || brandId)
      ))];

      navigate('/connectors', { 
        state: { 
          message: `Successfully created ${createdConnectors.length} Google Ads connector(s) across ${uniqueBrands.length} brand(s): ${uniqueBrands.join(', ')}`,
          type: 'success'
        }
      });

    } catch (err) {
      console.error('Error creating connectors:', err);
      setError(err.message || 'Failed to create connectors');
    } finally {
      setCreating(false);
    }
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => parseInt(b.id) === parseInt(brandId));
    return brand ? brand.name : 'Unknown Brand';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Google Ads accounts...</Typography>
      </Box>
    );
  }

  if (error && accounts.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/connectors')}>
          Back to Connectors
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <GoogleIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Select Google Ads Accounts
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Choose one or more Google Ads accounts to connect. You can assign each account to a different brand.
          </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {accounts.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No Google Ads accounts found. Make sure you have access to Google Ads accounts with your Google account.
        </Alert>
      ) : (
        <>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Available Accounts ({accounts.length})
          </Typography>

          <Grid container spacing={2}>
            {accounts.map((account) => (
              <Grid item xs={12} md={6} key={account.customerId}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedAccounts.some(a => a.customerId === account.customerId)}
                          onChange={(e) => handleAccountSelection(account.customerId, e.target.checked)}
                          disabled={creating}
                        />
                      }
                      label={
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {account.descriptiveName || `Account ${account.customerId}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ID: {account.customerId}
                          </Typography>
                          {account.alreadyConnected && (
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                label={account.connectedBrandName
                                  ? `Connected · ${account.connectedBrandName}`
                                  : 'Already connected'}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </Box>
                          )}
                          
                          <Divider sx={{ my: 1 }} />
                          
                          <Typography variant="body2" color="text.secondary">
                            Currency: {account.currencyCode}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Timezone: {account.timeZone}
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                            <Chip 
                              label={account.testAccount ? 'Test Account' : 'Live Account'} 
                              size="small" 
                              color={account.testAccount ? 'warning' : 'success'}
                            />
                          </Box>

                          {/* Brand Selection */}
                          {selectedAccounts.some(a => a.customerId === account.customerId) && (
                            <Box sx={{ mt: 2 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel id={`brand-select-${account.customerId}`}>Assign to Brand</InputLabel>
                                <Select
                                  labelId={`brand-select-${account.customerId}`}
                                  value={accountBrandMapping[account.customerId] || ''}
                                  label="Assign to Brand"
                                  onChange={(e) => handleBrandChange(account.customerId, e.target.value)}
                                  disabled={creating}
                                  sx={{ borderRadius: 1 }}
                                >
                                  {brands.map(brand => (
                                    <MenuItem key={brand.id} value={brand.id}>
                                      {brand.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {selectedAccounts.length} account(s) selected
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/connectors')}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateConnectors}
                disabled={selectedAccounts.length === 0 || creating}
                startIcon={creating ? <CircularProgress size={20} /> : null}
              >
                {creating ? 'Creating...' : `Create ${selectedAccounts.length} Connector(s)`}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default GoogleAdsSelection;